import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, detectUnconstructiveContent, containsProfanity, notifyMentorOfHarmDetection, sendNotification, sendPushToUser, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase, useRelationships } from '../shared.jsx';

function InfoCard({ label, value, className = '', colSpan = 1 }) {
  return (
    <div className={`p-2 sm:p-4 bg-stone-50 rounded-lg overflow-hidden ${colSpan === 2 ? 'sm:col-span-2' : ''}`}>
      <div className="text-xs sm:text-sm font-medium text-stone-500 mb-0.5 sm:mb-1">{label}</div>
      <div className={`text-sm sm:text-base text-stone-800 break-words ${className}`}>{value}</div>
    </div>
  );
}

// === MENTOR COMMUNICATION SYSTEM ===

// Visibility Selector — lets mentor choose who can see a message/comment
function MentorVisibilitySelector({ mentees, selected, onChange }) {
  const allSelected = selected.length === mentees.length;
  
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-stone-500">Visible to:</span>
      <button
        type="button"
        onClick={() => onChange(allSelected ? [] : mentees.map(m => m.id))}
        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
          allSelected ? 'bg-purple-100 border-purple-300 text-purple-700' : 'bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300'
        }`}
      >
        All Participants
      </button>
      {mentees.map(m => {
        const isSelected = selected.includes(m.id);
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => {
              if (isSelected) onChange(selected.filter(id => id !== m.id));
              else onChange([...selected, m.id]);
            }}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              isSelected ? 'bg-purple-100 border-purple-300 text-purple-700' : 'bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300'
            }`}
          >
            {m.name?.split(' ')[0] || m.email?.split('@')[0]}
          </button>
        );
      })}
    </div>
  );
}

// Renders a single mentor comment with visibility info
function MentorCommentBubble({ comment, currentUserId, mentees }) {
  const isMentor = comment.userId === currentUserId && comment.isMentorComment;
  const canSee = !comment.visibleTo || comment.visibleTo.includes(currentUserId) || comment.userId === currentUserId;
  if (!canSee) return null;

  const visibleNames = comment.visibleTo
    ? comment.visibleTo.length === mentees.length
      ? 'All Participants'
      : comment.visibleTo.map(id => {
          const m = mentees.find(x => x.id === id);
          return m?.name?.split(' ')[0] || 'Unknown';
        }).join(', ')
    : 'All Participants';

  return (
    <div className={`p-3 rounded-lg ${comment.isMentorComment ? 'bg-purple-50 border border-purple-200' : 'bg-stone-50'}`}>
      <div className="flex items-start gap-2">
        {comment.isMentorComment && (
          <div className="w-5 h-5 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 text-xs font-bold flex-shrink-0 mt-0.5">M</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-stone-600">{comment.userName}</span>
            {comment.isMentorComment && (
              <span className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded font-medium">Mentor</span>
            )}
            <span className="text-[10px] text-stone-500">{new Date(comment.timestamp).toLocaleString()}</span>
          </div>
          <p className="text-sm text-stone-700 mt-1 break-words">{comment.text}</p>
          {comment.isMentorComment && (
            <div className="text-[10px] text-purple-600 mt-1 flex items-center gap-1">
              <Eye className="w-3 h-3" />
              Visible to: {visibleNames}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Inline comment widget — attach mentor comments to any entry (paraphrase, Because, alternative)
function MentorInlineComment({ entryId, entryType, conflict, user, onUpdate }) {
  const toast = useToast();
  const showCrisisModal = useCrisisAlert();
  const [showForm, setShowForm] = useState(false);
  const [text, setText] = useState('');
  const [visibleTo, setVisibleTo] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const isMentor = user.role === 'mentor';
  const isResolved = conflict.status === 'resolved';
  const mentees = conflict.mentees || [];

  React.useEffect(() => {
    setVisibleTo(mentees.map(m => m.id));
  }, []);

  if (!isMentor) return null;

  const mentorComments = conflict.mentorComments || [];
  const entryComments = mentorComments.filter(c => c.entryId === entryId);

  // Show existing comments even when resolved, but hide the add button
  const handleSubmit = async () => {
    if (!text.trim()) return;
    if (visibleTo.length === 0) { toast('Select at least one participant who can see this comment'); return; }
    if (containsProfanity(text)) { toast('Please remove profanity from your comment'); return; }
    const toneCheck = detectUnconstructiveContent(text);
    if (toneCheck.isUnconstructive) { toast(toneCheck.reason || 'Please keep comments constructive.'); return; }
    setSubmitting(true);
    try {
      const newComment = {
        id: `mc-${Date.now()}`,
        entryId,
        entryType,
        userId: user.id,
        userName: user.name,
        isMentorComment: true,
        text: text.trim(),
        visibleTo: [...visibleTo],
        timestamp: new Date().toISOString()
      };
      const updated = {
        ...conflict,
        mentorComments: [...mentorComments, newComment],
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };
      addAuditEntry(updated, user.id, user.name, 'Added mentor comment', `On ${entryType}, visible to ${visibleTo.length === mentees.length ? 'all' : visibleTo.length + ' participant(s)'}`, user.email);
      await storage.set(`conflict:${conflict.id}`, updated, true);
      // Notify only the selected participants
      visibleTo.forEach(pid => {
        if (pid !== user.id) {
          sendPushToUser(pid, 'Mentor Comment', `${user.name} left feedback on your ${entryType} in "${conflict.title}"`, { conflictId: conflict.id });
        }
      });
      // Harm detection — check after saving (never block)
      const harmResult = detectHarmContent(text.trim());
      if (harmResult.detected) {
        showCrisisModal(harmResult.type);
        notifyMentorOfHarmDetection(conflict, user, harmResult.type, `mentor inline comment on ${entryType}`);
      }
      setText('');
      setShowForm(false);
      await onUpdate();
    } catch (e) {
      console.error('Error adding mentor comment:', e);
      toast('Failed to add comment');
    }
    setSubmitting(false);
  };

  return (
    <div className="mt-2">
      {entryComments.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {entryComments.filter(c => c.visibleTo?.includes(user.id) || c.userId === user.id).map(c => (
            <MentorCommentBubble key={c.id} comment={c} currentUserId={user.id} mentees={mentees} />
          ))}
        </div>
      )}
      {!isResolved && (
        !showForm ? (
          <button onClick={() => setShowForm(true)} className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> Mentor Comment
          </button>
        ) : (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm bg-white"
              placeholder="Add guidance or feedback..."
              rows={2}
              maxLength={500}
            />
            <MentorVisibilitySelector mentees={mentees} selected={visibleTo} onChange={setVisibleTo} />
            <div className="flex gap-2">
              <button onClick={handleSubmit} disabled={submitting || !text.trim() || visibleTo.length === 0}
                className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-stone-300 text-white text-xs rounded-lg font-medium transition-colors">
                {submitting ? 'Posting...' : 'Post Comment'}
              </button>
              <button onClick={() => { setShowForm(false); setText(''); }}
                className="px-3 py-1.5 text-stone-600 hover:bg-stone-100 text-xs rounded-lg transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );
}

// Inline comment display for mentees (read-only, shows only visible-to-them comments)
function MentorCommentsDisplay({ entryId, conflict, user }) {
  const mentorComments = conflict.mentorComments || [];
  const visible = mentorComments.filter(c => c.entryId === entryId && (c.visibleTo?.includes(user.id) || c.userId === user.id));
  const mentees = conflict.mentees || [];
  if (visible.length === 0) return null;

  return (
    <div className="mt-2 space-y-1.5">
      {visible.map(c => (
        <MentorCommentBubble key={c.id} comment={c} currentUserId={user.id} mentees={mentees} />
      ))}
    </div>
  );
}

// === MENTOR MESSAGING PANEL ===
// Direct back-and-forth communication between mentor and participants
function MentorMessagingPanel({ conflict, user, onUpdate, deactivatedUserIds = new Set() }) {
  const toast = useToast();
  const showCrisisModal = useCrisisAlert();
  const [message, setMessage] = useState('');
  const [visibleTo, setVisibleTo] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [chatDisabled, setChatDisabled] = useState(false);
  const [mentorIds, setMentorIds] = useState([]);
  const [mentorName, setMentorName] = useState(null);
  const messagesEndRef = React.useRef(null);
  const isMentor = user.role === 'mentor';
  const isMentee = conflict.mentees?.some(m => m.id === user.id);
  const mentees = conflict.mentees || [];
  const { relationships: allRelsFromContext } = useRelationships();

  // Derive mentor IDs from relationships (session-level context)
  React.useEffect(() => {
    const participantIds = mentees.map(m => m.id);
    const activeRels = (allRelsFromContext || []).filter(r => r.status === 'active' && r.type === 'mentor-participant');
    const ids = new Set();
    let name = null;
    activeRels.forEach(r => {
      const mid = r.mentorId || (r.initiatorRole === 'mentor' ? r.initiatorId : r.receiverId);
      const pid = r.participantId || (r.initiatorRole === 'participant' ? r.initiatorId : r.receiverId);
      if (participantIds.includes(pid)) {
        ids.add(mid);
        if (!name) name = r.initiatorRole === 'mentor' ? r.initiatorName : r.receiverName;
      }
    });
    setMentorIds([...ids]);
    setMentorName(name || 'Mentor');
  }, [conflict.id, allRelsFromContext]);

  React.useEffect(() => {
    setVisibleTo(mentees.map(m => m.id));
    // Feature 1: Check if mentor has disabled chat
    const settingsOwnerId = isMentor ? user.id : mentorIds[0];
    if (settingsOwnerId) {
      storage.get(`mentor-settings:${settingsOwnerId}`, true).then(settings => {
        if (!settings) return;
        const override = settings.conflictOverrides?.[conflict.id]?.chatEnabled;
        const effective = override !== undefined ? override : settings.chatEnabled;
        setChatDisabled(!effective);
      }).catch(() => {});
    }
  }, [conflict.id, mentorIds]);

  // Auto-scroll to bottom on new messages
  React.useEffect(() => {
    if (expanded && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conflict.mentorMessages?.length, expanded]);

  const allMessages = conflict.mentorMessages || [];
  // Filter messages visible to current user
  const visibleMessages = allMessages.filter(m =>
    m.userId === user.id || m.visibleTo?.includes(user.id)
  );
  const unreadCount = isMentee ? visibleMessages.filter(m => m.userId !== user.id && !m.readBy?.includes(user.id)).length : 0;

  const handleSend = async () => {
    if (!message.trim()) return;
    if (isMentor && visibleTo.length === 0) { toast('Select at least one recipient'); return; }
    if (containsProfanity(message)) { toast('Please remove profanity from your message'); return; }
    const toneCheck = detectUnconstructiveContent(message);
    if (toneCheck.isUnconstructive) { toast(toneCheck.reason || 'Please keep messages constructive.'); return; }
    setSubmitting(true);
    try {
      const newMsg = {
        id: `mm-${Date.now()}`,
        userId: user.id,
        userName: user.name,
        isMentorComment: isMentor,
        text: message.trim(),
        visibleTo: isMentor ? [...visibleTo] : [...mentees.map(m => m.id), ...mentorIds].filter(Boolean),
        readBy: [user.id],
        timestamp: new Date().toISOString()
      };
      const updated = {
        ...conflict,
        mentorMessages: [...allMessages, newMsg],
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };
      addAuditEntry(updated, user.id, user.name, isMentor ? 'Sent mentor message' : 'Replied to mentor',
        `Visible to ${isMentor ? (visibleTo.length === mentees.length ? 'all participants' : visibleTo.length + ' participant(s)') : 'mentor + all participants'}`,
        user.email
      );
      await storage.set(`conflict:${conflict.id}`, updated, true);
      // Notify recipients (only the selected visibleTo, not the sender)
      newMsg.visibleTo.forEach(pid => {
        if (pid !== user.id) {
          sendPushToUser(pid, isMentor ? 'Mentor Message' : 'New Message', `${user.name}: ${message.trim().substring(0, 80)}`, { conflictId: conflict.id });
        }
      });
      // Harm detection — check after saving (never block the user)
      const harmResult = detectHarmContent(message.trim());
      if (harmResult.detected) {
        showCrisisModal(harmResult.type);
        notifyMentorOfHarmDetection(conflict, user, harmResult.type, 'mentor chat message');
      }
      setMessage('');
      await onUpdate();
    } catch (e) {
      console.error('Error sending mentor message:', e);
      toast('Failed to send message');
    }
    setSubmitting(false);
  };

  // Mark messages as read when panel is opened
  const markAsRead = async () => {
    const unread = allMessages.filter(m => m.userId !== user.id && m.visibleTo?.includes(user.id) && !m.readBy?.includes(user.id));
    if (unread.length === 0) return;
    const updatedMessages = allMessages.map(m => {
      if (unread.find(u => u.id === m.id)) {
        return { ...m, readBy: [...(m.readBy || []), user.id] };
      }
      return m;
    });
    try {
      const updated = { ...conflict, mentorMessages: updatedMessages };
      await storage.set(`conflict:${conflict.id}`, updated, true);
      await onUpdate();
    } catch { /* silent */ }
  };

  if (!isMentor && !isMentee) return null;
  // Don't show mentor channel when no mentors are connected to this conflict
  if (!isMentor && mentorIds.length === 0) return null;

  // Feature 1: If chat is disabled by mentor and user is a mentee, show friendly disabled message
  if (chatDisabled && isMentee) {
    return (
      <div className="bg-white rounded-xl border-2 border-stone-200 overflow-hidden">
        <div className="p-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-stone-500 font-bold text-sm">M</div>
          <div>
            <div className="text-sm font-medium text-stone-500">Mentor Channel</div>
            <div className="text-xs text-stone-500">Chat is currently disabled by the mentor. They may enable it later in the process.</div>
          </div>
          <BellOff className="w-5 h-5 text-stone-300 ml-auto flex-shrink-0" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border-2 border-purple-200 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => {
          setExpanded(!expanded);
          if (!expanded) markAsRead();
        }}
        className="w-full p-4 flex items-center justify-between hover:bg-purple-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-sm">M</div>
          <div className="text-left">
            <div className="text-sm font-medium text-stone-800">
              Mentor Channel
              {isMentee && mentorName && <span className="text-xs text-stone-500 ml-1">({mentorName})</span>}
            </div>
            <div className="text-xs text-stone-500">
              {visibleMessages.length} message{visibleMessages.length !== 1 ? 's' : ''}
            </div>
          </div>
          {unreadCount > 0 && (
            <span className="bg-purple-600 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
              {unreadCount}
            </span>
          )}
        </div>
        <ChevronDown className={`w-5 h-5 text-stone-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t border-purple-100">
          {/* Messages */}
          <div className="max-h-80 overflow-y-auto p-4 space-y-3">
            {visibleMessages.length === 0 ? (
              <p className="text-stone-500 text-sm text-center py-4">
                {isMentor ? 'Send a message to guide the participants.' : 'No messages from the mentor yet.'}
              </p>
            ) : (
              visibleMessages.map(msg => {
                const isOwn = msg.userId === user.id;
                const visibleNames = msg.visibleTo
                  ? msg.visibleTo.length === mentees.length + (msg.isMentorComment ? 0 : 1)
                    ? 'Everyone'
                    : msg.visibleTo.filter(id => !mentorIds.includes(id)).map(id => {
                        const m = mentees.find(x => x.id === id);
                        return m?.name?.split(' ')[0] || 'Unknown';
                      }).join(', ')
                  : 'Everyone';
                return (
                  <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-xl px-4 py-2.5 ${
                      msg.isMentorComment
                        ? isOwn ? 'bg-purple-600 text-white' : 'bg-purple-100 text-stone-800'
                        : isOwn ? 'bg-amber-700 text-white' : 'bg-stone-100 text-stone-800'
                    }`}>
                      {!isOwn && (
                        <div className={`text-xs font-medium mb-1 ${msg.isMentorComment ? 'text-purple-700' : 'text-stone-500'}`}>
                          {msg.userName} {msg.isMentorComment ? '(Mentor)' : ''}{deactivatedUserIds.has(msg.userId) ? ' (deactivated)' : ''}
                        </div>
                      )}
                      <p className="text-sm break-words">{msg.text}</p>
                      <div className={`text-[10px] mt-1 flex items-center justify-between gap-2 ${
                        isOwn ? 'text-white/60' : msg.isMentorComment ? 'text-purple-600' : 'text-stone-500'
                      }`}>
                        <span>{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {msg.isMentorComment && (
                          <span className="flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" /> {visibleNames}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Compose — hidden when conflict is resolved */}
          {conflict.status !== 'resolved' ? (
            <div className="border-t border-purple-100 p-3 space-y-2">
              {isMentor && (
                <MentorVisibilitySelector mentees={mentees} selected={visibleTo} onChange={setVisibleTo} />
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  className="flex-1 px-3 py-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                  placeholder={isMentor ? 'Send guidance...' : 'Reply to mentor...'}
                  maxLength={500}
                />
                <button
                  onClick={handleSend}
                  disabled={submitting || !message.trim() || (isMentor && visibleTo.length === 0)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-stone-300 text-white rounded-lg text-sm font-medium transition-colors flex-shrink-0"
                >
                  Send
                </button>
              </div>
            </div>
          ) : (
            <div className="border-t border-purple-100 p-3">
              <p className="text-xs text-stone-500 text-center">This conflict has been resolved. Chat is read-only.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Conflict Process Component

export { InfoCard, MentorVisibilitySelector, MentorCommentBubble, MentorInlineComment, MentorCommentsDisplay, MentorMessagingPanel };
